'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BellRing, HeartPulse, Mail, Phone, Shirt, UserRound } from 'lucide-react';
import type { LocationPlace, MedicalData, Person, ProfileMe } from '@/lib/types';
import { isUnauthorized } from '@/lib/api';
import { KIND_LABEL, fmtDate, fmtShortDate, isPast, memberSubline } from '@/lib/format';
import { useProfile } from './ProfileContext';
import { Avatar } from './Avatar';
import { PhotoAvatar } from './PhotoAvatar';
import { useMedical } from './medicalStore';
import { MemberSheet, type MemberPage } from './MemberSheet';
import { SubscriptionsControls } from './LoopSection';
import { HomeCard } from './HomeSection';
import { GroupsCard } from './GroupsSection';
import { GivingCard } from './GivingSection';
import { InvoicesCard } from './InvoicesSection';
import { useFaceSlide } from './faceSlide';
import { ArchiveLink, FormerMembers, GUEST_CHILD_POSITION_ID } from './FormerMembers';
import { FamilyCard } from './FamilyCard';
import { setRelateDraft } from './FamilyPages';
import { groupByRelationshipType } from './grouping';
import { findSpouse, isMarried } from './spouse';
import { Bar, DISPLAY, Fact, FactCard, FactGrid, Masonry, MasonryItem, Pii } from './ui';
import { EmergencyContactRow, MedicalChips } from '@church/nextjs-ui/website/profile';

/**
 * The household is the centre of the page. "Profile" big up top, then the
 * selected person (photo and name, nothing the cards already say) with the
 * rest of the household as a strip of faces beside them when there is
 * room, under them when there is not. Tap a face and it slides into the
 * hero spot while the previous one slides back into the strip
 * (`useFaceSlide`). The cards under it are that person's details, with
 * the household's Home card, Giving for every head of household (it is the
 * household's giving), and for the signed-in person Groups and Invoices:
 * one grid, no more sections. The sheets do the
 * editing.
 */
export function PeopleSection({
  locations,
  mapsApiKey,
}: {
  locations: LocationPlace[];
  mapsApiKey: string | undefined;
}) {
  const { api, data, setRequests } = useProfile();
  const people = [data.me, ...data.members];
  const [selectedId, setSelectedId] = useState(data.me.contactId);
  const slide = useFaceSlide(selectedId);
  const select = (contactId: number) => {
    if (contactId === selectedId) return;
    slide.snapshot();
    setSelectedId(contactId);
  };
  const person = people.find((p) => p.contactId === selectedId) ?? data.me;
  const isMe = person.contactId === data.me.contactId;
  // Giving is the household's, given under one account: it shows for every
  // head of household (position 1; position 2 is Minor Child), not only me.
  const headOfHousehold = isMe || person.householdPositionId === 1;
  const [page, setPage] = useState<MemberPage | null>(null);
  // Bumped after an archive so Former members re-reads.
  const [formerVersion, setFormerVersion] = useState(0);

  // Relationship requests (Neon) for my own Family card and page.
  useEffect(() => {
    const controller = new AbortController();
    api
      .getRequests(controller.signal)
      .then((r) => {
        if (controller.signal.aborted) return;
        // Only what is still waiting — an answered request is history, and an
        // accepted one is already on the Family card.
        const pending = (list: typeof r.incoming) => list.filter((x) => x.status === 'pending');
        setRequests({ incoming: pending(r.incoming), outgoing: pending(r.outgoing) });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setRequests(isUnauthorized(err) ? { incoming: [], outgoing: [] } : 'error');
      });
    return () => controller.abort();
  }, [api, setRequests]);

  const others = people.filter((p) => p.contactId !== person.contactId);

  return (
    <section className="flex flex-col gap-6">
      <h1 className={`${DISPLAY} text-6xl leading-[0.9] text-[#1c2b39] @xl:text-7xl`}>Profile</h1>

      <div
        ref={slide.root}
        className="flex flex-col gap-6 @3xl:flex-row @3xl:items-center @3xl:justify-between @3xl:gap-8"
      >
        <SelectedPerson
          person={person}
          isMe={isMe}
          action={
            !isMe && person.householdPositionId === GUEST_CHILD_POSITION_ID ? (
              <ArchiveLink
                person={person}
                onArchived={() => {
                  select(data.me.contactId);
                  // Give the save a moment before Former members re-reads.
                  setTimeout(() => setFormerVersion((v) => v + 1), 1500);
                }}
              />
            ) : null
          }
        />
        {others.length > 0 && <PersonStrip people={others} me={data.me} onSelect={select} />}
      </div>
      <FormerMembers version={formerVersion} />

      <div className="flex flex-col gap-4">
        <p
          key={person.contactId}
          className={`${DISPLAY} swap-in px-1 text-2xl leading-none text-[#1c2b39]`}
        >
          {isMe ? (
            'Your details'
          ) : (
            <>
              <Pii>{person.firstName}</Pii>
              's details
            </>
          )}
        </p>
        <Masonry>
          {/* The person's own cards swap in with them; the money, home and groups cards stay put. */}
          {/* About and Family share one cell so Family always sits under About —
              as separate cells the masonry would put Family in the next column.
              The cell measures its first child, so they stack in one wrapper. */}
          <MasonryItem key={`about-${person.contactId}`} className="swap-in">
            <div className="flex flex-col gap-4">
              <AboutCard
                person={person}
                isMe={isMe}
                onEdit={() => setPage('about')}
                onContact={() => setPage('contact')}
              />
              <FamilyCard
                person={person}
                onManage={() => setPage('family')}
                onRelate={(other) => {
                  setRelateDraft(other);
                  setPage('relate');
                }}
              />
            </div>
          </MasonryItem>
          <MasonryItem key={`subscriptions-${person.contactId}`} className="swap-in">
            <SubscriptionsCard person={person} />
          </MasonryItem>
          {headOfHousehold && (
            <MasonryItem>
              <GivingCard />
            </MasonryItem>
          )}
          <MasonryItem key={`medical-${person.contactId}`} className="swap-in">
            <MedicalCard
              person={person}
              onOpen={() => setPage('medical')}
              onEmergency={() => setPage('emergency')}
            />
          </MasonryItem>
          <MasonryItem>
            <HomeCard locations={locations} mapsApiKey={mapsApiKey} />
          </MasonryItem>
          {isMe && (
            <MasonryItem>
              <GroupsCard />
            </MasonryItem>
          )}
          {isMe && (
            <MasonryItem>
              <InvoicesCard />
            </MasonryItem>
          )}
        </Masonry>
      </div>

      <MemberSheet
        contactId={page ? person.contactId : null}
        defaultPage={page ?? 'about'}
        onClose={() => setPage(null)}
      />
    </section>
  );
}

/**
 * The selected person, big: the photo (tap to change it) and the name.
 * Campus, email and phone live on the cards, so they are not repeated here.
 */
function SelectedPerson({
  person,
  isMe,
  action,
}: {
  person: Person;
  isMe: boolean;
  /** A quiet action under the subline (Archive, for a Guest Child). */
  action?: ReactNode;
}) {
  const { update } = useProfile();
  const setImage = (imageUrl: string | null) =>
    update((d) =>
      isMe
        ? { ...d, me: { ...d.me, imageUrl } }
        : {
            ...d,
            members: d.members.map((m) =>
              m.contactId === person.contactId ? { ...m, imageUrl } : m
            ),
          }
    );
  const sub = isMe ? 'You' : memberSubline(person);
  return (
    <div className="flex min-w-0 items-center gap-5 @xl:gap-7">
      <div data-face={person.contactId} className="shrink-0 origin-top-left">
        <PhotoAvatar person={person} size={128} onChange={setImage} />
      </div>
      <div key={person.contactId} className="swap-in min-w-0">
        <h2 className={`${DISPLAY} text-5xl leading-[0.9] text-[#1c2b39] @xl:text-6xl`}>
          <Pii>{person.displayName}</Pii>
        </h2>
        <p
          className={`pii mt-2 text-sm font-semibold ${!isMe && !person.relationship ? 'text-[#299c8f]' : 'text-gray-500'}`}
        >
          {sub || (isMe ? 'You' : 'How related?')}
        </p>
        {action}
      </div>
    </div>
  );
}

/**
 * The rest of the household as faces, bucketed by Relationship_Type in
 * MP's order: primary family first, then "Extended Family", "In-Laws" and
 * so on under a small label, so a niece in the house does not read as a
 * daughter. The selected person is not here; they are the hero.
 */
function PersonStrip({
  people,
  me,
  onSelect,
}: {
  people: Person[];
  me: Person;
  onSelect: (contactId: number) => void;
}) {
  const { data } = useProfile();
  const groups = groupByRelationshipType(
    people,
    (p) => (p.contactId === me.contactId ? null : p.relationship),
    data.lookups
  );
  return (
    <div
      role="tablist"
      aria-label="Household"
      className="scrollbar-hide -mx-1 flex max-w-full min-w-0 gap-3 overflow-x-auto px-1 py-1 @3xl:justify-end"
    >
      {groups.map((g, gi) => (
        <div
          key={g.key}
          className={`flex shrink-0 flex-col gap-1 ${gi > 0 ? 'border-l border-gray-200 pl-3' : ''}`}
        >
          {groups.length > 1 && (
            <span className="px-2 text-[11px] font-bold tracking-widest text-gray-400 uppercase">
              {g.label}
            </span>
          )}
          <div className="flex gap-1">
            {g.items.map((p) => {
              const isMe = p.contactId === me.contactId;
              const sub = isMe ? 'You' : memberSubline(p);
              return (
                <button
                  key={p.contactId}
                  type="button"
                  role="tab"
                  aria-selected={false}
                  onClick={() => onSelect(p.contactId)}
                  className="flex w-[96px] shrink-0 flex-col items-center gap-2 rounded-3xl px-2 py-3 text-center transition-all hover:scale-[1.03] hover:bg-white/70 active:scale-[0.98]"
                >
                  <span data-face={p.contactId} className="block origin-top-left rounded-full">
                    <Avatar name={p.displayName} imageUrl={p.imageUrl} size={64} />
                  </span>
                  <span className="flex max-w-full min-w-0 flex-col">
                    <span className="pii truncate text-sm font-bold text-[#1c2b39]">
                      {p.firstName}
                    </span>
                    <span
                      className={`pii truncate text-[11px] ${!isMe && !p.relationship ? 'font-semibold text-[#299c8f]' : 'text-gray-500'}`}
                    >
                      {sub || (isMe ? 'You' : 'How related?')}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const lookup = (options: { id: number; label: string }[], id: number | null) =>
  options.find((o) => o.id === id)?.label ?? null;

function fullName(person: ProfileMe['me'] | Person, data: ProfileMe): string {
  const { lookups } = data;
  const me = 'prefixId' in person ? person : null;
  return [
    me ? lookup(lookups.prefixes, me.prefixId) : null,
    person.firstName,
    me?.middleName ?? null,
    person.lastName,
    me ? lookup(lookups.suffixes, me.suffixId) : null,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Who the person is, then how to reach them as a section of its own (a tap
 * there opens the same form scrolled to the contact fields). Who their
 * people are is the Family card under it.
 */
function AboutCard({
  person,
  isMe,
  onEdit,
  onContact,
}: {
  person: Person;
  isMe: boolean;
  onEdit: () => void;
  onContact: () => void;
}) {
  const { data } = useProfile();
  const { lookups } = data;
  const home = data.household?.homePhone ?? null;
  const work = isMe ? data.me.workPhone : null;
  const shirt = lookup(lookups.shirtSizes, person.shirtSizeId);
  const me = isMe ? data.me : null;
  const married = me ? isMarried(me.maritalStatusId) : false;
  const spouse = married ? findSpouse(data) : null;
  const maritalLabel = me ? lookup(lookups.maritalStatuses, me.maritalStatusId) : null;

  return (
    <FactCard
      title="About"
      icon={<UserRound className="h-4 w-4" strokeWidth={2.5} />}
      onEdit={onEdit}
    >
      <FactGrid>
        <Fact label="Name" value={<Pii>{fullName(person, data)}</Pii>} className="@xs:col-span-2" />
        {person.nickname && person.nickname !== person.firstName && (
          <Fact label="Goes by" value={<Pii>{person.nickname}</Pii>} />
        )}
        <Fact
          label="Birthday"
          value={
            person.dateOfBirth ? (
              <Pii>
                {fmtDate(person.dateOfBirth)}
                {person.age !== null ? ` · ${person.age}` : ''}
              </Pii>
            ) : null
          }
        />
        <Fact label="Gender" value={lookup(lookups.genders, person.genderId)} />
        {me ? (
          <Fact
            label="Marital status"
            value={
              maritalLabel ? (
                <span className="flex flex-col gap-1">
                  <span>{maritalLabel}</span>
                  {spouse ? (
                    <span className="inline-flex items-center gap-1.5 text-gray-600">
                      <Avatar name={spouse.displayName} imageUrl={spouse.imageUrl} size={20} />
                      <span className="pii truncate">{spouse.displayName}</span>
                    </span>
                  ) : married ? (
                    <span className="text-xs font-semibold text-[#299c8f]">
                      Who are you married to? Edit to say.
                    </span>
                  ) : null}
                </span>
              ) : null
            }
          />
        ) : (
          <Fact label="In the household" value={person.position} missing="Not set" />
        )}
        <Fact
          label="Occupation"
          value={person.occupation ? <Pii>{person.occupation}</Pii> : null}
        />
        <Fact
          label="Shirt size"
          value={
            shirt ? (
              <span className="inline-flex items-center gap-1.5">
                <Shirt className="h-3.5 w-3.5 text-gray-400" strokeWidth={2.5} />
                {shirt}
              </span>
            ) : null
          }
        />
      </FactGrid>
      {/* Contact: its own section, opening the form at the contact fields. */}
      <div
        className="-mx-2 flex flex-col gap-3 rounded-2xl border-t border-gray-100 px-2 pt-3 pb-1"
        onClick={(e) => {
          e.stopPropagation();
          onContact();
        }}
      >
        <span className="flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-[#299c8f] uppercase">
          <Phone className="h-3.5 w-3.5" strokeWidth={2.5} />
          Contact
        </span>
        <FactGrid>
          <Fact
            label="Email"
            className="@xs:col-span-2"
            value={
              person.email ? (
                <span className="inline-flex max-w-full items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" strokeWidth={2.5} />
                  <span className="pii truncate">{person.email}</span>
                </span>
              ) : null
            }
          />
          <Fact label="Mobile" value={person.mobilePhone && <Pii>{person.mobilePhone}</Pii>} />
          {work && <Fact label="Work" value={<Pii>{work}</Pii>} />}
          {home && <Fact label="Home phone" value={<Pii>{home}</Pii>} />}
        </FactGrid>
      </div>
    </FactCard>
  );
}

/**
 * What the person gets from the church and who can find them: Email
 * Subscriptions with every list, texting and the directory, each a switch
 * that writes from the card itself. Nothing here opens a sheet.
 */
function SubscriptionsCard({ person }: { person: Person }) {
  return (
    <FactCard title="Subscriptions" icon={<BellRing className="h-4 w-4" strokeWidth={2.5} />}>
      <SubscriptionsControls person={person} />
    </FactCard>
  );
}

/**
 * A glance at the medical card: who to call first (the emergency contact,
 * a row of its own that opens that editor; in life it belongs with the
 * medical card, whatever table MP keeps it in), then what is on file and
 * when it was last confirmed. The sheet holds the rest.
 */
function MedicalCard({
  person,
  onOpen,
  onEmergency,
}: {
  person: Person;
  onOpen: () => void;
  onEmergency: () => void;
}) {
  const { api, data } = useProfile();
  const ec = person.emergencyContact;
  const linked = ec?.contactId
    ? [data.me, ...data.members].find((p) => p.contactId === ec.contactId)
    : null;
  // Shared with the Medical page in the sheet, so a save there shows here at once.
  const medical = useMedical(api, person.contactId);

  let body: ReactNode;
  if (medical === null) {
    body = (
      <div className="flex flex-col gap-2" aria-busy="true">
        <Bar className="h-3 w-40" />
        <Bar className="h-3 w-28" />
      </div>
    );
  } else if (medical === 'error') {
    body = <p className="text-sm text-gray-500">We couldn't load the medical card right now.</p>;
  } else if (medical.unavailable) {
    body = (
      <p className="text-sm text-gray-500">
        Not available here for <Pii>{person.firstName}</Pii>.
      </p>
    );
  } else if (medical.items.length === 0 && !medical.profile) {
    body = (
      <p className="text-sm text-gray-600">
        Nothing on file. Allergies, medications, an EpiPen or inhaler, and the card camps ask for.
      </p>
    );
  } else {
    const reviewed = medical.profile?.lastReviewedAt;
    body = (
      <div className="flex flex-col gap-2.5">
        {medical.items.length > 0 && (
          <MedicalChips
            items={medical.items.map((m) => ({
              id: m.id,
              label: m.label,
              kindLabel: KIND_LABEL[m.kind],
              severe: m.severe,
              expires: m.expiresOn
                ? { expired: isPast(m.expiresOn), label: fmtShortDate(m.expiresOn) }
                : null,
            }))}
          />
        )}
        <p className="text-xs text-gray-500">
          {[
            medical.profile?.insuranceCarrier ? 'Insurance on file' : 'No insurance on file',
            medical.profile?.physicianName ? 'physician on file' : null,
            reviewed ? `confirmed ${fmtShortDate(reviewed)}` : 'not yet confirmed',
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    );
  }

  return (
    <FactCard
      title="Medical"
      icon={<HeartPulse className="h-4 w-4" strokeWidth={2.5} />}
      onEdit={onOpen}
      editLabel="Open"
    >
      <EmergencyContactRow
        contact={
          ec
            ? {
                kind: ec.kind,
                name: linked?.displayName ?? ec.name,
                imageUrl: linked?.imageUrl ?? ec.imageUrl ?? null,
              }
            : null
        }
        detail={[
          ec?.phone ?? linked?.mobilePhone,
          linked?.relationship?.label ?? (linked?.contactId === data.me.contactId ? 'You' : null),
        ]
          .filter(Boolean)
          .join(' · ')}
        prompt={
          <>
            Who should we call about <Pii>{person.firstName}</Pii>?
          </>
        }
        onClick={onEmergency}
      />
      <div className="pii border-t border-gray-100 pt-3">{body}</div>
    </FactCard>
  );
}
